import {
  solutionsStructure as ptStructure,
  MarketKey,
  MarketStructure,
  ServiceData,
  MarketData
} from './solutions.pt';

// Market titles/descriptions in English. Category and service names in the
// structure are already in English, so we reuse the PT tree and only swap
// the market-level copy.
const marketCopyEn: Record<MarketKey, { title: string; desc: string }> = {
  'operadoras-isps': {
    title: 'For carriers and ISPs',
    desc: 'Protection, observability and performance for the networks that sustain connectivity.'
  },
  'operadores-servicos-nuvem': {
    title: 'For cloud service operators',
    desc: 'Protection and analytics for digital environments and distributed infrastructures.'
  },
  'redes-corporativas': {
    title: 'For corporate networks',
    desc: 'Reliability, security and specialized operation for enterprise IT.'
  },
  'ti-corporativa': {
    title: 'For corporate IT',
    desc: 'Operational efficiency and full visibility for IT and security teams.'
  }
};

export const solutionsStructure: MarketStructure[] = ptStructure.map((market) => ({
  ...market,
  title: marketCopyEn[market.id].title,
  desc: marketCopyEn[market.id].desc
}));

export const servicesData: Record<string, ServiceData> = {
  'advanced-edge-core-observability-b2c': {
    title: "TE - Advanced Edge & Core Observability for B2C Providers",
    slug: 'advanced-edge-core-observability-b2c',
    sku: "B2C-TECH-MINDVIEW",
    subtitle: "Leverage real-time subscriber insights to drive revenue",
    tagline: null,
    description: "This TechEnabler solution was created by combining Kentik technology with TechEnabler engineering, and customized specifically for B2C providers that require advanced edge and core observability. It combines real-time traffic analysis with user experience insights, allowing service providers to optimize the quality of experience for end customers.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability"],
    targetAudience: {
      idealClient: "B2C providers, residential ISPs, mobile operators focused on end customers, and digital service providers.",
      useCases: ["individual user experience analysis, CDN/OTT service optimization, zero-rating program management, planning of personalized offers."]
    },
    benefits: ["granular per-subscriber visibility, retention insights, revenue optimization, improved digital experience."],
    competitiveAdvantages: [],
    uniqueValue: "MTTR reduction (fast resolution of issues affecting user experience), NPS increase, revenue growth through customized services, churn reduction.",
    keyFeatures: ["Subscriber Intelligence, CDN & OTT Analytics, Synthetics Testing."],
    technicalDetails: ["massive processing, granular multidimensional analysis, forensic data retention, open APIs via REST-API. Service based on the Kentik platform, TechEnabler SOC and TechEnabler Product Engineering."],
    commercialModels: ["OPEX: SaaS, as a Service."],
    image: "/technabler-solucao-network-edge-&-core-observability-for-providers.webp"
  },
  'advanced-edge-core-observability-transit': {
    title: "TE - Advanced Edge & Core Observability for Transit Providers",
    slug: 'advanced-edge-core-observability-transit',
    sku: "T-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "This TechEnabler solution was designed by combining Kentik and Corero technologies with TechEnabler engineering. It was developed specifically for IP transit providers that need complete visibility and advanced analysis of their backbone infrastructure and transit connections. The solution optimizes operations through a network intelligence platform with advanced peering and interconnection capabilities, making it possible to build business cases based on interconnection data to reduce costs and increase performance.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability"],
    targetAudience: {
      idealClient: "transit providers, Tier 1 networks, global backbone operators, international connectivity providers.",
      useCases: ["transit cost optimization, peering and interconnection analysis, market intelligence, capacity planning, SLA monitoring."]
    },
    benefits: ["transit cost reduction, interconnection optimization, global backbone visibility, competitive intelligence."],
    competitiveAdvantages: [],
    uniqueValue: "cost reduction, revenue growth, integrated AI.",
    keyFeatures: ["Peering Analytics with integrated peering DB data, Network Cost Intelligence, BGP monitoring, Synthetics Testing."],
    technicalDetails: ["multiprotocol support for NetFlow, sFlow, J-Flow, IPFIX, NetStream, Cflow, forensic data retention, high capacity, no limit on the number of interfaces. Service based on the Kentik platform, TechEnabler SOC and TechEnabler Product Engineering."],
    commercialModels: ["OPEX: SaaS, as a Service."],
    image: "/technabler-solucao-network-edge-&-core-observability-for-providers.webp"
  },
  'cloud-analytics-digital-enterprises': {
    title: "TE - Cloud Analytics for Digital Enterprises",
    slug: 'cloud-analytics-digital-enterprises',
    sku: "TECH-ANALYTICS",
    subtitle: null,
    tagline: null,
    description: "This TechEnabler solution combines the Kentik platform with specialized services to deliver Cloud Performance Analytics and CDN/OTT Analytics, providing an answer for digital service providers that need visibility of both cloud and on-premises structures. It enables mapping of customers' digital supply chains across more than 350 OTT applications and CDNs.",
    primaryMarket: 'operadores-servicos-nuvem',
    relatedMarkets: ["ti-corporativa"],
    technicalCategories: ["Cloud Performance Monitoring"],
    image: "/technabler-solucao-cloud-analytics-for-digital-enterprises.webp",
    targetAudience: {
      idealClient: "digital enterprises operating in cloud environments, regional datacenters, local cloud providers, digital companies.",
      useCases: ["CDN and OTT optimization, cloud monitoring, cost analysis, marketing intelligence."]
    },
    benefits: ["unified cloud visibility, advanced OTT Analytics, business intelligence."],
    competitiveAdvantages: ["massive scale, integrated AI, local expertise."],
    uniqueValue: "cloud cost optimization, complexity reduction.",
    keyFeatures: ["advanced CDN Analytics (maps the supply chain across more than 350 CDNs), cloud monitoring, performance analysis."],
    technicalDetails: ["multi-cloud support, DPI-free analytics, multidimensional analysis."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'end-to-end-observability-providers': {
    title: "TE - End-to-End Observability for Providers",
    slug: 'end-to-end-observability-providers',
    sku: "H-TECH-MINDFULL",
    subtitle: null,
    tagline: null,
    description: "This TechEnabler solution combines the Kentik, Corero and Unryo systems with TechEnabler specialized services, delivering end-to-end network slicing plus proactive visibility and monitoring of the user experience. The solution operates across multi-cloud and hybrid scenarios. It was specially developed for providers facing growing network complexity and rising user expectations.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Next Generation Observability"],
    image: "/technabler-solucao-end-to-end-observability-for-providers.webp",
    targetAudience: {
      idealClient: "large service providers whose networks are evolving and being modernized.",
      useCases: ["operational optimization, infrastructure modernization, proactive monitoring, advanced analytics."]
    },
    benefits: ["end-to-end observability, proactive monitoring, operational efficiency, strategic visibility."],
    competitiveAdvantages: ["single platform, integrated AI, local expertise, specialized SOCaaS."],
    uniqueValue: "cost reduction, SLA improvement, team efficiency, proven ROI.",
    keyFeatures: ["multilayer monitoring, advanced automation, integrated analysis."],
    technicalDetails: ["massive capacity, multiprotocol support, unlimited scalability."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'full-stack-observability-platform': {
    title: "TE - Full Stack Observability Platform",
    slug: 'full-stack-observability-platform',
    sku: "TECH-MINDFULL",
    subtitle: null,
    tagline: null,
    description: "This TechEnabler solution combines the Kentik, Corero and Unryo platforms with specialized services to deliver Total Intelligence and all Network Telemetry Unified. Using large-scale machine learning, it identifies anomalies in real time, offering a service within the SDOM concept (Smart Data Observability per Metrics) and thus ensuring unified visibility of cloud and on-premises devices.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Next Generation Observability","Cloud Performance Monitoring"],
    image: "/technabler-solucao-full-stack-observability-platform.webp",
    targetAudience: {
      idealClient: "digital cloud providers, corporate networks and service providers in general.",
      useCases: ["end-to-end observability, multilayer monitoring, consolidation of tools and systems."]
    },
    benefits: ["unified visibility, massive AI, cost consolidation, multi-service observability."],
    competitiveAdvantages: ["unified multi-level and multi-service view."],
    uniqueValue: "operational efficiency, MTTR reduction, strategic view of systems and services.",
    keyFeatures: ["data enrichment, AI integration, multilayer monitoring, overall AI-driven data consolidation."],
    technicalDetails: ["multiprotocol support, unlimited scalability, multidimensional analysis, full set of APIs."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'hybrid-observability-digital-enterprises': {
    title: "TE - Hybrid Observability for Digital Enterprises",
    slug: 'hybrid-observability-digital-enterprises',
    sku: "H-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "This TechEnabler solution combines the Kentik platform with specialized services to deliver complete observability across hybrid multi-cloud environments. It provides unified visibility across multi-cloud and hybrid on-premises networks and datacenters, offering synthetic tests and traffic flow analysis in a single system. The solution is designed to serve digital corporations of all sizes.",
    primaryMarket: 'operadores-servicos-nuvem',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability","Cloud Performance Monitoring"],
    image: "/technabler-solucao-hybrid-observability-for-digital-enterprises.webp",
    targetAudience: {
      idealClient: "digital enterprises, corporate networks, companies with hybrid multi-cloud infrastructure.",
      useCases: ["hybrid and multi-cloud monitoring, cloud cost optimization, digital experience monitoring, advanced troubleshooting."]
    },
    benefits: ["unified visibility, cost reduction, operational efficiency, consolidation of tools and systems."],
    competitiveAdvantages: ["AI-driven insights, local support."],
    uniqueValue: "MTTR reduction, high productivity, strategic visibility.",
    keyFeatures: ["hybrid and multi-cloud monitoring, performance monitoring, cross-functional monitoring (infrastructure, CDNs, multi-cloud and SaaS applications)."],
    technicalDetails: ["multiprotocol, AI capabilities, open APIs."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'integration-solution-development': {
    title: "TE - Integration and Solution Development",
    slug: 'integration-solution-development',
    sku: "TECH-SERVICES",
    subtitle: null,
    tagline: null,
    description: "These are specialized services in the TechEnabler portfolio, designed to give our customers comfort and peace of mind when implementing network protection and observability solutions. TechEnabler has complete mastery of every possible integration scenario for the technologies included in its projects.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas","ti-corporativa"],
    technicalCategories: ["Professional Services & Integration"],
    image: "/technabler-solucao-integration-and-solution-development.webp",
    targetAudience: {
      idealClient: "digital enterprises operating in cloud environments, regional datacenters, local cloud providers, digital companies.",
      useCases: ["technology integration, custom development, regulatory compliance."]
    },
    benefits: ["consolidated expertise, end-to-end integration, strategic partnerships."],
    competitiveAdvantages: ["specialized SOC, full customization, native integrations, multilingual support."],
    uniqueValue: "operational optimization, project deployment control, time-to-market.",
    keyFeatures: ["API availability, custom development, multi-tenant portal, detailed reporting."],
    technicalDetails: ["webhooks for notifications, open SQL queries, hybrid implementations."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'local-scrubbing-as-a-service-lsaas': {
    title: "TE - Local Scrubbing as a Service - LSaaS",
    slug: 'local-scrubbing-as-a-service-lsaas',
    sku: "ISP-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "LSaaS is a business model developed by TechEnabler to make premium DDoS defense technology more accessible. We have built high-capacity mitigation centers at strategic points in Brazil. Mitigation clusters are currently deployed in São Paulo, Rio de Janeiro and Fortaleza, with others under evaluation. Our customers connect directly to these centers, sending their traffic and receiving it back clean and attack-free. Because it is a direct connection, it works as if the center belonged to their own backbone — in practice, a new network element connected locally to their router. This is an Opex business model with monthly payments. Customers can start small, allocating a fraction of the available mitigation capacity and increasing that reservation as their network grows. There is no wasted investment: customers pay only for the resources they use.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-local-scrubbing-as-a-service-lsaas.webp",
    targetAudience: {
      idealClient: "mid-sized companies with link capacity that want to offer premium protection; IT corporations and digital enterprises that do not need to invest in purchasing this equipment.",
      useCases: ["blocking DDoS attacks."]
    },
    benefits: ["no added latency, Always-On protection, no leaks, no broken connections."],
    competitiveAdvantages: ["premium local anti-DDoS solution with excellent cost-benefit."],
    uniqueValue: "we can implement customer portals (sub-tenants) to create revenue-generating products.",
    keyFeatures: ["the customer access portal provides all attack data and is updated in real time. The solution includes a SOC service for configuring blocking rules and providing follow-up during attacks."],
    technicalDetails: ["the customer must request a cross-connection at the datacenter. The traffic forwarded to the scrubbing center is decided by the customer."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'network-edge-core-observability-providers': {
    title: "TE - Network Edge & Core Observability for Providers",
    slug: 'network-edge-core-observability-providers',
    sku: "ISP-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "This TechEnabler solution adds intelligence to observability capabilities. By combining Kentik technology with TechEnabler engineering, it delivers an observability and artificial intelligence system built around the challenges faced by ISPs. The result is a network intelligence platform specifically dedicated to modern infrastructure teams, enabling complete visibility of hybrid and multi-cloud networks. The solution unifies and correlates cloud data, devices, flows and synthetic data on a single platform.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability"],
    image: "/technabler-solucao-network-edge-&-core-observability-for-providers.webp",
    targetAudience: {
      idealClient: "ISPs, telecom carriers, Tier 1 and Tier 2 companies.",
      useCases: ["network traffic analysis, capacity planning, peering and interconnection optimization, connectivity and transit cost analysis, proactive network monitoring, DDoS detection and mitigation."]
    },
    benefits: ["faster deployment than traditional tools, massive scalability with unlimited flow volume, intuitive interface with an AI module, local support through the TechEnabler SOC."],
    competitiveAdvantages: ["100% cloud-based, work accelerated by integrated AI."],
    uniqueValue: "connectivity cost reduction, 360-degree visibility for proactive decisions. Time savings in diagnosis and response, failure anticipation, revenue generation via the TechEnabler multi-tenant portal.",
    keyFeatures: ["compatible with NetFlow, sFlow, IPFIX, SNMP and BGP; data enrichment with geolocation, peering DB and business data, machine learning, open APIs, custom dashboards."],
    technicalDetails: ["service based on the Kentik platform and TechEnabler SOC."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'managed-traffic-analysis': {
    title: "TE - Managed Traffic Analysis",
    slug: 'managed-traffic-analysis',
    sku: "TECH-TRAFFIC MANAGER",
    subtitle: null,
    tagline: null,
    description: "This TechEnabler solution combines the Kentik platform with TechEnabler SOCaaS services to deliver continuous, intelligent network traffic analysis. It enables Network Capacity Planning, peering and interconnection optimization, as well as OTT/CDN Analytics through a comprehensive menu of network analysis and diagnostics parameters. The service includes the TechEnabler SOCaaS Services Operations Center for 24x7 support.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Next Generation Observability"],
    image: "/technabler-solucao-managed-traffic-analysis.webp",
    targetAudience: {
      idealClient: "ISPs, telecom carriers and digital companies that need continuous traffic analysis with specialized support.",
      useCases: ["managed traffic analysis, operational cost optimization, strategic decision support, CDN and OTT analysis, managed capacity planning."]
    },
    benefits: ["complete managed service, dedicated SOC, operational intelligence, specialized local support."],
    competitiveAdvantages: ["integration with multiple technologies, consolidated expertise."],
    uniqueValue: "cost reduction, time optimization, strategic visibility.",
    keyFeatures: ["full L3/L4 flow analysis with capacity planning, synthetic monitoring for service validation, process automation, digital experience analysis."],
    technicalDetails: ["multiprotocol support, open APIs, massive scalability."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'mssp-services': {
    title: "TE - MSSP Services",
    slug: 'mssp-services',
    sku: "TECH-MSSP",
    subtitle: null,
    tagline: null,
    description: "These services combine SOCaaS Services Operations Center expertise with advanced technologies to provide world-class DDoS protection and observability. We integrate world-class SOC Services in 24x7x365 mode through strategic partnerships with technology vendors, offering multi-tenant protection and observability.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-mssp-services.webp",
    targetAudience: {
      idealClient: "ISPs and digital companies that need intensive monitoring of their protection mechanisms.",
      useCases: ["Always-On protection, managed services provider, multi-tenant portal."]
    },
    benefits: ["protection without downtime, constant monitoring and support, scalability, complete visibility."],
    competitiveAdvantages: ["our team holds MSSP certification, providing the highest level of knowledge and quality in service delivery."],
    uniqueValue: "MTTR reduction and TCO optimization.",
    keyFeatures: ["SecureWatch Analytics, multi-tenant portal, automated provisioning."],
    technicalDetails: ["extremely high available capacity (tens of terabits per second)."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'no-appliance-detection': {
    title: "TE - No Appliance Detection",
    slug: 'no-appliance-detection',
    sku: "NA-TECH-DETECTOR",
    subtitle: "Protect your network with no upfront investment",
    tagline: null,
    description: "TechEnabler's No Appliance Detection solution provides continuous network traffic monitoring by analyzing flows generated by the customer's network routers. It automatically identifies and classifies DDoS attacks in real time, allowing the customer to take the actions needed to prevent damage to the network. The solution is delivered as a service, including TechEnabler consulting, design and implementation, with no Capex investment required from the customer.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-no-appliance-detection.webp",
    targetAudience: {
      idealClient: "service providers of all sizes, and mid-sized to large companies that need to protect their links and critical applications against DDoS attacks without investing in dedicated infrastructure.",
      useCases: ["DDoS attack detection, anomalous traffic monitoring, threat reporting and support for the Security Operation Center (SOC) team."]
    },
    benefits: ["no perceptible latency, Always-On protection with detection in seconds, real-time dashboards and proactive alerts."],
    competitiveAdvantages: ["scalable, flexible platform, long-term raw data retention, multi-tenant integration, APIs for automation and orchestration, detailed reporting."],
    uniqueValue: "time savings in diagnosis, failure anticipation, revenue generation via multi-tenant portal, increased security with no physical assets required.",
    keyFeatures: ["compatible with NetFlow, sFlow, IPFIX, SNMP and BGP; including sampled monitoring and data collection across physical, virtual and cloud environments."],
    technicalDetails: ["service based on the Kentik platform and TechEnabler SOC."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'no-appliance-protection': {
    title: "TE - No Appliance Protection",
    slug: 'no-appliance-protection',
    sku: "NA-TECH-PROTECTOR",
    subtitle: "Turn your routers into your first line of defense",
    tagline: null,
    description: "TechEnabler's No Appliance Protection solution provides continuous network traffic monitoring and mitigation by analyzing flows generated by the customer's network routers. It automatically identifies, classifies and mitigates DDoS attacks in real time by exporting policies directly to the customer's routers. The solution is delivered as a service, including TechEnabler consulting, design and implementation, with no Capex investment required from the customer.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-no-appliance-protection.webp",
    targetAudience: {
      idealClient: "service providers of all sizes, mid-sized to large companies that need to protect their links and critical applications against DDoS attacks through automatic mitigation actions, without investing in dedicated infrastructure.",
      useCases: ["automatic DDoS attack detection and response, anomalous traffic monitoring, threat reporting and support for the Security Operation Center (SOC) team."]
    },
    benefits: ["no perceptible latency, Always-On protection with detection and response in seconds, real-time dashboards and proactive alerts."],
    competitiveAdvantages: ["scalable, flexible platform, long-term raw data retention, multi-tenant integration, APIs for automation and orchestration, detailed reporting."],
    uniqueValue: "time savings in diagnosis and response, failure anticipation, revenue generation via multi-tenant portal, increased security with no physical assets required.",
    keyFeatures: ["compatible with NetFlow, sFlow, IPFIX, SNMP and BGP; including sampled monitoring and data collection across physical, virtual and cloud environments."],
    technicalDetails: ["service based on the Kentik platform and TechEnabler SOC."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'smart-dns-corporations': {
    title: "TE - Smart DNS for Corporations",
    slug: 'smart-dns-corporations',
    sku: "TECH-SMART-DNS-CORP",
    subtitle: null,
    tagline: null,
    description: "This service, developed by TechEnabler, uses Planisys secure DNS technology to deliver a solution tailored to the needs of companies that want to protect their network's outbound traffic through PDNS and RPZ (Response Policy Zone).",
    primaryMarket: 'redes-corporativas',
    relatedMarkets: [],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-smart-dns-for-corporations.webp",
    targetAudience: {
      idealClient: "companies of all sizes that want a safe browsing strategy, blocking access to malicious sites with processes kept up to date by global cybersecurity databases.",
      useCases: ["protecting users against dangerous links; protecting IoT systems; protection against phishing and malware; customer-managed RPZ resolvers; blocking policies; DNS black lists; native compliance with ANATEL blocking requirements."]
    },
    benefits: ["transparent service for the user; configuration without service interruption; permanently updated software; centralized management system; dedicated installation per protection profile."],
    competitiveAdvantages: ["no application installation required; simple web-based management system; high-level management supported by TechEnabler with reports and analytics."],
    uniqueValue: "simple operation with no need for specialized staff; the security solution can be offered to every user on the company network.",
    keyFeatures: ["PDNS/RPZ with threat intelligence."],
    technicalDetails: ["the RPZ software is installed together with the DNS. One RPZ is installed for each protection profile defined by the customer. Cloud installation with no impact on the customer's network."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'smart-dns-isps': {
    title: "TE - Smart DNS for ISPs",
    slug: 'smart-dns-isps',
    sku: "TECH-SMART-DNS-ISP",
    subtitle: null,
    tagline: null,
    description: "This service, developed by TechEnabler, uses Planisys secure DNS technology to deliver a solution tailored to the needs of ISPs that want to protect the outbound traffic of both their own network and their customers' networks through PDNS and RPZ (Response Policy Zone).",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Network Defense"],
    targetAudience: {
      idealClient: "ISPs and telecom carriers.",
      useCases: ["protecting users against dangerous links; protecting IoT systems; protection against phishing and malware; ISP-managed RPZ resolvers; blocking policies; DNS black lists; native compliance with ANATEL blocking requirements; reselling the protection service to the ISP's end customers."]
    },
    benefits: ["transparent service for the user; configuration without service interruption; permanently updated software; customizable centralized management system."],
    competitiveAdvantages: ["multi-tenant system; each DNS installation has its own RPZ; minimal configuration on the existing network; only the router needs to point to the RPZ address."],
    uniqueValue: "simple operation with no need for specialized staff; the security solution can be sold to the ISP's end customers (monetizable solution).",
    keyFeatures: ["PDNS/RPZ with threat intelligence."],
    technicalDetails: ["the RPZ software is installed together with the DNS. One RPZ is installed for each protection profile defined by the customer. On-premises, cloud or hybrid installation."],
    commercialModels: ["OPEX: SaaS, as a Service."],
    image: "/technabler-solucao-smart-dns-isps-arquitetura.webp"
  },
  'soc-services': {
    title: "TE - SOC Services",
    slug: 'soc-services',
    sku: "TECH-SOC",
    subtitle: null,
    tagline: null,
    description: "TechEnabler has developed a new Service Operations Center business model designed to deliver visibility and proactive protection through several integrated technologies. We are pioneers of the SOC as a Service model, offering complete network observability through AIOPS techniques.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-soc-services.webp",
    targetAudience: {
      idealClient: "ISPs, carriers and digital companies that need to outsource SOC work, or want a top-level team supporting their local staff.",
      useCases: ["24x7 proactive monitoring, DDoS attack detection, behavioral traffic analysis."]
    },
    benefits: ["protection without downtime, Always-On, with local support."],
    competitiveAdvantages: ["World-Class SOC Service with 24x7x365 support."],
    uniqueValue: "capability to implement an Incident Monitoring Portal for the customer's infrastructure and for end customers, generating revenue in the multi-tenant model.",
    keyFeatures: ["AI-powered analytics system and multi-tenant portal."],
    technicalDetails: ["service delivered through the combination of Kentik, Corero and Unryo technologies and the TechEnabler SOC."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'cloud-defense': {
    title: "TE - Cloud Defense",
    slug: 'cloud-defense',
    sku: "TECHCLOUD",
    subtitle: "TechEnabler's cloud-based anti-DDoS service",
    tagline: null,
    description: "The TechCloud service is TechEnabler's Cloud Scrubbing. It is a DDoS attack mitigation cloud with premium quality across all of its elements, from the transit providers to the routing equipment and mitigation system. This scrubbing can be connected via ASN 274830, and, with local partnerships, we can deliver across virtually all of Latin America.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-cloud-defense.webp",
    targetAudience: {
      idealClient: "companies of any size that want cloud-based protection.",
      useCases: ["blocking DDoS attacks without impacting their own infrastructure."]
    },
    benefits: ["greater mitigation power and efficiency, real-time analysis portal, access to a broad transport network, defense from layer three up to layer seven."],
    competitiveAdvantages: ["we can provide a detector to automate the announcement process to the solution."],
    uniqueValue: "greater mitigation efficiency and premium TechEnabler 24x7 support.",
    keyFeatures: ["Always-On Protection; On-Demand Protection."],
    technicalDetails: ["Register the AS or AS-Cone in RADb, with activation within minutes; choose the clean traffic delivery method: PNI, IX or GRE."],
    commercialModels: ["Protection Service."]
  }
};

export const marketsData: MarketData[] = [
  {
    key: 'operadoras-isps' as MarketKey,
    title: 'For carriers and ISPs',
    slug: '/mercados/operadoras-isps',
    description: 'Security, observability and performance for the networks that sustain connectivity.',
    fullDescription: 'For carriers and internet service providers, network availability and security are the business itself. TechEnabler offers solutions that protect the infrastructure against attacks, automate problem mitigation and expand operational visibility.',
    challenges: [
      'Protection against massive DDoS attacks',
      'Peering and IP transit optimization',
      'Monetization of security services (value-added services)',
      'Global backbone observability'
    ]
  },
  {
    key: 'operadores-servicos-nuvem' as MarketKey,
    title: 'For cloud service operators',
    slug: '/mercados/operadores-servicos-nuvem',
    description: 'Protection and analytics for digital environments and distributed infrastructures.',
    fullDescription: 'Cloud operators and data centers demand absolute resilience and a precise reading of how their resources are used. Our cloud defense, advanced analytics and full-stack observability solutions ensure the digital environment is always protected.',
    challenges: [
      'Unified multi-cloud visibility',
      'OTT/CDN application performance',
      'Scalable infrastructure security',
      'Digital experience monitoring'
    ]
  },
  {
    key: 'redes-corporativas' as MarketKey,
    title: 'For corporate networks',
    slug: '/mercados/redes-corporativas',
    description: 'Reliability, security and specialized operation for the IT of large organizations.',
    fullDescription: 'Companies across industries depend on robust corporate networks to sustain their operations. TechEnabler helps corporate IT implement DNS-based defense layers, integrate network technologies and predict failures with AIOps.',
    challenges: [
      'Outbound traffic security (DNS)',
      'Protection of critical assets without hardware Capex',
      'Hybrid network observability',
      'IoT device monitoring'
    ]
  },
  {
    key: 'ti-corporativa' as MarketKey,
    title: 'For corporate IT',
    slug: '/mercados/ti-corporativa',
    description: 'Operational efficiency and full visibility for IT and security teams.',
    fullDescription: 'IT and security teams need tools that automate diagnostics and provide clear visibility into application and infrastructure performance. TechEnabler delivers advanced observability and professional services to optimize corporate operations.',
    challenges: [
      'MTTR reduction in incidents',
      'Traffic compliance and auditing',
      'Simplified tool management',
      'Proactive anomaly detection'
    ]
  }
];
